import { DownloadOutlined } from '@ant-design/icons';
import { App, Button, Card, Grid, Table, type TableColumnsType } from 'antd';
import { useMemo, useState } from 'react';
import type { TaskStatisticsOutputDto } from '../model';
import { exportCreditStatistics } from '../utils/export-credit-statistics';
import { accountNameColumn } from './account-name-column';

const columns: TableColumnsType<
  TaskStatisticsOutputDto['account_stats'][number]
> = [
  { ...accountNameColumn, key: 'account_name' },
  {
    title: '每日信用点额度',
    dataIndex: 'credit',
    render: (credit: number | undefined) =>
      credit === -1 ? '无限额度' : credit === 0 ? '无额度' : (credit ?? '-'),
  },
  {
    title: '信用点使用量',
    dataIndex: 'cost',
  },
];

export function CreditStatisticsTable(props: {
  data: TaskStatisticsOutputDto['account_stats'];
  rangeTime?: TaskStatisticsOutputDto['range_time'];
  loading?: boolean;
}) {
  const { xs } = Grid.useBreakpoint();
  const { message } = App.useApp();
  const [exporting, setExporting] = useState(false);
  const [accountFilter, setAccountFilter] = useState('');
  const data = useMemo(
    () => [...props.data].sort((a, b) => b.cost - a.cost),
    [props.data],
  );

  const exportData = data.filter((row) =>
    row.account_name.toLowerCase().includes(accountFilter.trim().toLowerCase()),
  );

  const handleExport = async () => {
    if (!props.rangeTime || props.loading || exporting || !exportData.length)
      return;
    setExporting(true);
    try {
      await exportCreditStatistics(exportData, props.rangeTime);
    } catch (error) {
      console.error('信用点统计导出失败', error);
      message.error('信用点统计导出失败，请重试');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card
      title="信用点统计"
      extra={
        <Button
          icon={<DownloadOutlined />}
          loading={exporting}
          disabled={props.loading || !props.rangeTime || !exportData.length}
          onClick={handleExport}
        >
          导出 Excel
        </Button>
      }
    >
      <Table
        rowKey={(record) => record.created_by ?? record.account_id}
        size="small"
        scroll={xs ? { x: 400 } : void 0}
        columns={columns}
        onChange={(_, filters) =>
          setAccountFilter(String(filters['account_name']?.[0] ?? ''))
        }
        dataSource={data}
        pagination={{ defaultPageSize: 10 }}
      />
    </Card>
  );
}
